import type { DentalXChangeDentalSummaryInfo } from '@frontend/api-insurance-eligibility-report';
import { i18next } from '@frontend/i18n';
import { TableColumnConfig } from '@frontend/design-system';
import {
  getAdditionalInformationColumnConfig,
  getAmountColumnConfig,
  getAuthorizationRequiredColumnConfig,
  getBenefitAgeColumnConfig,
  getCodeColumnConfig,
  getCoverageLevelColumnConfig,
  getInsuranceTypeColumnConfig,
  getLimitationColumnConfig,
  getMessageColumnConfig,
  getNetworkColumnConfig,
  getPercentColumnConfig,
  getPlanCoverageDescriptionColumnConfig,
  getPlanPeriodColumnConfig,
  getServiceDatesColumnConfig,
  getServiceTypeColumnConfig,
  InsuranceReportTableColumnKeysType,
} from '../../components/InsuranceTables/insurance-reports-table-columns.helper';
import { TableDataType, TablesDataType } from '../../types/types';

export const getAllInsuranceReportTableColumnKeysForDentalXChange = (): InsuranceReportTableColumnKeysType[] => {
  return [
    {
      id: 'code',
      label: i18next.t('Code', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'serviceType',
      label: i18next.t('Service Type', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'network',
      label: i18next.t('Network', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'coverageLevel',
      label: i18next.t('Coverage Level', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'additionalInformation',
      label: i18next.t('Additional Information', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'planPeriod',
      label: i18next.t('Plan Period', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'amount',
      label: i18next.t('Amount', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'planCoverageDescription',
      label: i18next.t('Description', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'message',
      label: i18next.t('Message', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'benefitAge',
      label: i18next.t('Benefit Age', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'percent',
      label: i18next.t('Percent', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'authorizationRequired',
      label: i18next.t('Authorization Required', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'insuranceType',
      label: i18next.t('Insurance Type', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'serviceDates',
      label: i18next.t('Service Dates', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'secondaryIDs',
      label: i18next.t('Secondary IDs', { ns: 'insuranceEligibilityReport' }),
    },
    {
      id: 'limitation',
      label: i18next.t('Limitation', { ns: 'insuranceEligibilityReport' }),
    },
  ];
};

export const getCoInsuranceTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('120'),
    getCodeColumnConfig('50'),
    getPercentColumnConfig('50'),
    getMessageColumnConfig(),
    getCoverageLevelColumnConfig('70'),
    getInsuranceTypeColumnConfig(),
  ];
};

export const getDeductibleTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('100'),
    getCodeColumnConfig('50'),
    getAmountColumnConfig('70'),
    getPlanPeriodColumnConfig('100'),
    getMessageColumnConfig(),
    getPlanCoverageDescriptionColumnConfig('100'),
  ];
};

export const getActiveCoverageTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('120'),
    getInsuranceTypeColumnConfig('150'),
    getPlanCoverageDescriptionColumnConfig('120'),
    getCoverageLevelColumnConfig('120'),
    getMessageColumnConfig(),
    getNetworkColumnConfig('70'),
  ];
};

export const getNoCoveredTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getCodeColumnConfig('50'),
    getCoverageLevelColumnConfig('70'),
    getMessageColumnConfig(),
    getAuthorizationRequiredColumnConfig('100'),
  ];
};

export const getCoPaymentTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getCodeColumnConfig('50'),
    getCoverageLevelColumnConfig('70'),
    getMessageColumnConfig(),
    getPlanPeriodColumnConfig(),
  ];
};

export const getBenefitDescriptionTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getMessageColumnConfig(),
    getPlanPeriodColumnConfig(),
    getCoverageLevelColumnConfig('70'),
    getPlanCoverageDescriptionColumnConfig(),
  ];
};

export const getExclusionsTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getCodeColumnConfig('50'),
    getBenefitAgeColumnConfig('50'),
    getMessageColumnConfig(),
    getPlanCoverageDescriptionColumnConfig(),
  ];
};

export const getOtherPayorTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [getPlanCoverageDescriptionColumnConfig(), getMessageColumnConfig(), getNetworkColumnConfig()];
};

export const getOtherBenefitTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getCodeColumnConfig('50'),
    getMessageColumnConfig(),
    getPlanCoverageDescriptionColumnConfig(),
    getInsuranceTypeColumnConfig(),
    getServiceDatesColumnConfig('100'),
  ];
};

export const getOutOfPocketTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getAmountColumnConfig('50'),
    getCoverageLevelColumnConfig('70'),
    getPlanPeriodColumnConfig(),
    getInsuranceTypeColumnConfig(),
    getMessageColumnConfig(),
  ];
};

export const getMaximumTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getCodeColumnConfig('50'),
    getAmountColumnConfig('50'),
    getMessageColumnConfig(),
    getCoverageLevelColumnConfig('70'),
    getPlanPeriodColumnConfig('70'),
  ];
};

export const getLimitationTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [
    getServiceTypeColumnConfig('70'),
    getCodeColumnConfig('50'),
    getLimitationColumnConfig('100'),
    getMessageColumnConfig(),
    getServiceDatesColumnConfig('120'),
    getNetworkColumnConfig('70'),
  ];
};

export const getOtherSourceOfDataTableColumns = (): TableColumnConfig<TableDataType>[] => {
  return [getAdditionalInformationColumnConfig(), getMessageColumnConfig()];
};

//Below are the tables which will be shown for dentalXchange data
export const getInsuranceReportTableColumnsForDentalXChange = (
  tableName: keyof TablesDataType,
  keys: (keyof DentalXChangeDentalSummaryInfo)[]
) => {
  switch (tableName) {
    case 'activeCoverage':
      return keys.includes('activeCoverage') ? getActiveCoverageTableColumns() : [];
    case 'coInsurance':
      return keys.includes('coInsurance') ? getCoInsuranceTableColumns() : [];
    case 'coPayment':
      return keys.includes('coPayment') ? getCoPaymentTableColumns() : [];
    case 'deductible':
      return keys.includes('deductible') ? getDeductibleTableColumns() : [];
    case 'exclusions':
      return keys.includes('exclusions') ? getExclusionsTableColumns() : [];
    case 'limitation':
      return keys.includes('limitation') ? getLimitationTableColumns() : [];
    case 'maximum':
      return keys.includes('maximum') ? getMaximumTableColumns() : [];
    case 'notCovered':
      return keys.includes('notCovered') ? getNoCoveredTableColumns() : [];
    case 'otherPayor':
      return keys.includes('otherPayor') ? getOtherPayorTableColumns() : [];
    case 'otherBenefit':
      return keys.includes('otherBenefit') ? getOtherBenefitTableColumns() : [];
    case 'outOfPocket':
      return keys.includes('outOfPocket') ? getOutOfPocketTableColumns() : [];
    case 'otherSourceOfData':
      return keys.includes('otherSourceOfData') ? getOtherSourceOfDataTableColumns() : [];
    default:
      return getCoInsuranceTableColumns();
  }
};
